import { useEffect, useRef, useState } from "react";
import type { ReferenceDocumentView } from "@/lib/api/generated/casedb";
import { fetchReferenceDocument } from "@/lib/api/referenceDocuments";

export interface ReferenceDocumentLinksState {
  loadingId: number | null;
  error: string | null;
  openDocument: (document: ReferenceDocumentView) => Promise<void>;
}

/**
 * Document-opening state shared by every screen that lists local reference documents.
 *
 * <p>Uses authenticated fetching before opening a local reference; never navigates to a bare API
 * URL. Object URLs are tracked and revoked on unmount so a long review session does not leak blobs.
 */
export function useReferenceDocumentLinks(): ReferenceDocumentLinksState {
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const urls = useRef<string[]>([]);
  useEffect(() => () => { urls.current.forEach((url: string) => URL.revokeObjectURL(url)); }, []);

  async function openDocument(document: ReferenceDocumentView): Promise<void> {
    const tab = window.open("about:blank", "_blank");
    if (!tab) { setError("Allow a new tab to open this reference document."); return; }
    tab.opener = null;
    setLoadingId(document.document_id ?? null);
    setError(null);
    try {
      const blob: Blob = await fetchReferenceDocument(document);
      const url: string = URL.createObjectURL(blob);
      urls.current.push(url);
      const page: number | undefined = document.page_numbers?.[0];
      tab.location.href = `${url}${blob.type === "application/pdf" && page ? `#page=${page}` : ""}`;
    } catch {
      tab.close();
      setError(`Unable to open ${document.file_name ?? "reference document"}.`);
    } finally { setLoadingId(null); }
  }

  return { loadingId, error, openDocument };
}
